// src/contexts/produtos/ProdutoContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { putData, deleteData } from '../../services/Api';
import { useMessage } from '../geral/MessageContext';
import { useAlert } from '../geral/AlertContext';
import { copiarProdutoVariacoes } from '../../services/produtos/ProdutoVariacaoService';
import ProdutoSearch from '../../pages/produtos/produtos/ProdutoSearch';
import ProdutoSearchDelivery from '../../pages/produtos/produtos/ProdutoSearchDelivery';
import {
    adicionarProduto,
    alterarFavorito as alterarFavoritoService,
    alterarVendeAppProduto,
    alterarEsgotadoProduto,
    alterarInativoProduto,
    atualizarProduto,
    obterProdutos,
    obterProdutosFavoritos,
} from '../../services/produtos/ProdutoService';

const ProdutoContext = createContext();

export const useProduto = () => useContext(ProdutoContext);

export const ProdutoProvider = ({ children }) => {
    const [produtos, setProdutos] = useState([]);
    const [produtosFavoritos, setProdutosFavoritos] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();
    const [showSearchModalProduto, setShowSearchModalProduto] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const tempOnSelectProdutoRef = useRef(null);

    const normalizeDecimal = (value) => {
        if (value === null || value === undefined || value === '') {
            return value;
        }

        if (typeof value === 'number') {
            return String(value);
        }

        return value.replace('.', '').replace(',', '.');
    };

    const normalizeCashBackNumber = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const number = Number(value);
        return number > 0 ? number : null;
    };

    const ajustaPayload = (produto) => {
        const payload = { ...produto };

        payload.preco = normalizeDecimal(produto.preco);
        payload.custoUltimo = normalizeDecimal(produto.custoUltimo);
        payload.custoMedio = normalizeDecimal(produto.custoMedio);
        payload.estoqueMin = normalizeDecimal(produto.estoqueMin);   
        payload.aliquotaIss = normalizeDecimal(produto.aliquotaIss);
        payload.aliquotaPis = normalizeDecimal(produto.aliquotaPis);
        payload.aliquotaCofins = normalizeDecimal(produto.aliquotaCofins);
        payload.cashBackPercent = normalizeCashBackNumber(produto.cashBackPercent);

        return payload;
    };

    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                const [result, favoritos] = await Promise.all([
                    obterProdutos(),
                    obterProdutosFavoritos(),
                ]);
                setProdutos(result || []);
                setProdutosFavoritos(favoritos || []);
            } catch (error) {
                console.error('Erro ao buscar produtos:', error);
                showAlert('Erro ao buscar produtos!', 'danger');
            }
        };

        fetchDataFromAPI();
    }, []);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const addProduto = async (produto) => {
        try {
            const payload = ajustaPayload(produto);
            const result = await adicionarProduto(payload);
            if (produto.produtoVariacoes?.length) result.produtoVariacoes = await copiarProdutoVariacoes(result.id, produto.produtoVariacoes);

            setProdutos((produtosAtuais) => [...produtosAtuais, result]);
            showAlert('Produto adicionado com sucesso!');
            return result;
        } catch (error) {
            console.error('Erro ao adicionar produto:', error);
            showMessage(error.message || 'Erro ao adicionar produto', 'danger');
            return null;
        }
    };

    const updateProduto = async (produto) => {
        try {
            const payload = ajustaPayload(produto);
            const result = await atualizarProduto(payload);

            const index = produtos.findIndex(p => p.id === result.id);
            const updatedProdutos = [...produtos];
            if (index !== -1) {
                updatedProdutos[index] = result;
            }

            setProdutos(updatedProdutos);
            showAlert('Produto atualizado com sucesso!');
            return result;
        } catch (error) {
            console.error('Erro ao atualizar produto:', error);
            showMessage(error.message || 'Erro ao atualizar produto', 'danger');
            return null;
        }
    };

    const updateProdutoOrderNum = async (produtoId, orderNum) => {
        try {
            const produtoAtual = produtos.find((item) => item.id === produtoId);
            if (!produtoAtual) {
                return null;
            }

            const payload = ajustaPayload({
                ...produtoAtual,
                grupoId: produtoAtual.grupo?.id || null,
                subGrupoId: produtoAtual.subGrupo?.id || null,
                marcaId: produtoAtual.marca?.id || null,
                unTrib: produtoAtual.unMedida?.sigla || produtoAtual.unTrib || null,
                destaqueProdutoId: produtoAtual.destaqueProduto?.id || produtoAtual.destaqueProdutoId || null,
                orderNum,
            });

            delete payload.grupo;
            delete payload.subGrupo;
            delete payload.marca;
            delete payload.unMedida;

            delete payload.destaqueProduto;

            const result = await putData('/Produto', payload);

            const index = produtos.findIndex((item) => item.id === result.id);
            const updatedProdutos = [...produtos];

            if (index !== -1) {
                updatedProdutos[index] = result;
            }

            setProdutos(updatedProdutos);
            showAlert('Ordem alterada com sucesso!');
            return result;
        } catch (error) {
            console.error('Erro ao atualizar ordem do produto:', error);
            showMessage('Erro ao atualizar ordem do produto', 'danger');
            return null;
        }
    };

    const alteraVendeApp = async (produtoId, vendeApp) => {
        try {
            await alterarVendeAppProduto(produtoId, vendeApp);
            setProdutos(lista => lista.map(p => p.id === produtoId ? { ...p, vendeApp } : p));
            setProdutosFavoritos(lista => lista.map(p => p.id === produtoId ? { ...p, vendeApp } : p));
            showAlert('Produto atualizado com sucesso!');
            return true;
        } catch (error) {
            console.error('Erro ao atualizar produto:', error);
            showMessage('Erro ao atualizar produto', 'danger');
            return false;
        }
    };

    const alteraEsgotado = async (produtoId, esgotado) => {
        try {
            await alterarEsgotadoProduto(produtoId, esgotado);
            setProdutos(lista => lista.map(p => p.id === produtoId ? { ...p, esgotado } : p));
            setProdutosFavoritos(lista => lista.map(p => p.id === produtoId ? { ...p, esgotado } : p));
            showAlert('Produto atualizado com sucesso!');
            return true;
        } catch (error) {
            console.error('Erro ao atualizar produto:', error);
            showMessage('Erro ao atualizar produto', 'danger');
            return false;
        }
    };

    const alteraInativo = async (produtoId, inativo) => {
        try {
            await alterarInativoProduto(produtoId, inativo);
            setProdutos(lista => lista.map(p => p.id === produtoId ? { ...p, inativo } : p));
            setProdutosFavoritos(lista => lista.map(p => p.id === produtoId ? { ...p, inativo } : p));
            showAlert('Produto atualizado com sucesso!');
            return true;
        } catch (error) {
            console.error('Erro ao atualizar produto:', error);
            showMessage('Erro ao atualizar produto', 'danger');
            return false;
        }
    };

    const alteraFavorito = async (produtoId, favorito) => {
        try {
            await alterarFavoritoService(produtoId, favorito);

            setProdutos(prev => prev.map(produto =>
                produto.id === produtoId ? { ...produto, favorito } : produto
            ));

            setProdutosFavoritos(prev => {
                if (!favorito) {
                    return prev.filter(produto => produto.id !== produtoId);
                }

                const produto = produtos.find(item => item.id === produtoId);
                if (!produto || prev.some(item => item.id === produtoId)) {
                    return prev;
                }

                return [...prev, { ...produto, favorito: true }];
            });

            showAlert(favorito
                ? 'Produto adicionado aos favoritos!'
                : 'Produto removido dos favoritos!');
        } catch (error) {
            console.error('Erro ao alterar favorito:', error);
            showMessage('Erro ao alterar favorito', 'danger');
        }
    };

    const deleteProduto = async (id) => {
        try {
            await deleteData('/Produto', { produtoId: id });
            setProdutos(produtos.filter((p) => p.id !== id));
            showAlert('Produto excluído com sucesso!');
        } catch (error) {
            console.error('Erro ao excluir produto:', error);
            showMessage(`Erro ao excluir produto!\n${error}`, 'danger');
        }
    };

    const handleCloseSearchModal = () => {
        setShowSearchModalProduto(false);
        tempOnSelectProdutoRef.current = null;
    };

    const handleSelectProduto = () => {
        setShowSearchModalProduto(false);
    };

    const showProdutoSearch = () => new Promise((resolve) => {
        setShowSearchModalProduto(true);

        const handleSelect = (produtoSelect) => {
            setShowSearchModalProduto(false);
            resolve(produtoSelect || null);
        };

        tempOnSelectProdutoRef.current = handleSelect;
    });

    const handleSelectProdutoSearch = (produtoSelect) => {
        const onSelect = tempOnSelectProdutoRef.current || handleSelectProduto;
        onSelect(produtoSelect);
    };

    return (
        <ProdutoContext.Provider
            value={{
            atualizarVariacoesProduto: (produtoId, produtoVariacoes) => {
                setProdutos(lista => lista.map(p => Number(p.id) === Number(produtoId) ? { ...p, produtoVariacoes } : p));
                setProdutosFavoritos(lista => lista.map(p => Number(p.id) === Number(produtoId) ? { ...p, produtoVariacoes } : p));
            },
                produtos,
                setProdutos,
                produtosFavoritos,
                setProdutosFavoritos,
                showProdutoSearch,
                addProduto,
                updateProduto,
                updateProdutoOrderNum,
                deleteProduto,
                alteraVendeApp,
                alteraEsgotado,
                alteraInativo,
                alteraFavorito,
            }}
        >
            {children}

            {isMobile ? (
                <ProdutoSearch
                    show={showSearchModalProduto}
                    onHide={handleCloseSearchModal}
                    onSelectProduto={handleSelectProdutoSearch}
                />
            ) : (
                <ProdutoSearchDelivery
                    show={showSearchModalProduto}
                    onHide={handleCloseSearchModal}
                    onSelectProduto={handleSelectProdutoSearch}
                />
            )}
        </ProdutoContext.Provider>
    );
};
